import React from 'react';
import { Printer, FileText } from 'lucide-react';
import { useTheme } from '../../../hooks/useTheme';
import type { DdtPrintTemplate } from '../../../lib/stores/ddtPrintTemplateStore';

interface DdtPrintTemplateModalProps {
  isOpen: boolean;
  templates: DdtPrintTemplate[];
  /** template scelto, oppure null = layout standard (flusso legacy) */
  onSelect: (template: DdtPrintTemplate | null) => void;
  onClose: () => void;
  /** Template già associato ai documenti in stampa (print_template_id) — evidenziato
   *  come scelta consigliata, ma resta comunque cambiabile cliccando un'altra opzione */
  defaultTemplateId?: string;
}

export function DdtPrintTemplateModal({ isOpen, templates, onSelect, onClose, defaultTemplateId }: DdtPrintTemplateModalProps) {
  const { isDark } = useTheme();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative rounded-xl shadow-xl p-6 max-w-sm w-full ${isDark ? 'bg-gray-900 border border-gray-700' : 'bg-white'}`}>
        <h3 className={`text-base font-semibold mb-1 ${isDark ? 'text-gray-100' : 'text-[#37352F]'}`}>Layout di stampa</h3>
        <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-[#787774]'}`}>
          Scegli il template con cui stampare il documento.
        </p>
        <div className="flex flex-col gap-1.5">
          {templates.map(t => {
            const isDefault = t.id === defaultTemplateId;
            return (
              <button
                key={t.id}
                onClick={() => onSelect(t)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left text-sm transition-colors ${
                  isDefault
                    ? isDark ? 'border-[#2383E2] bg-blue-500/10 text-gray-200' : 'border-[#2383E2] bg-blue-50 text-[#37352F]'
                    : isDark ? 'border-gray-700 text-gray-200 hover:bg-gray-800' : 'border-[rgba(55,53,47,0.16)] text-[#37352F] hover:bg-[rgba(55,53,47,0.04)]'
                }`}
              >
                {t.logo_url ? (
                  <img src={t.logo_url} alt="" className="w-7 h-7 object-contain rounded" />
                ) : (
                  <Printer className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-[#787774]'}`} />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{t.name}</div>
                  {t.company_name && t.company_name !== t.name && (
                    <div className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-[#787774]'}`}>{t.company_name}</div>
                  )}
                </div>
                {isDefault && (
                  <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-[#2383E2]'}`}>Associato</span>
                )}
              </button>
            );
          })}
          <button
            onClick={() => onSelect(null)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left text-sm transition-colors ${
              isDark
                ? 'border-gray-700 text-gray-400 hover:bg-gray-800'
                : 'border-[rgba(55,53,47,0.16)] text-[#787774] hover:bg-[rgba(55,53,47,0.04)]'
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>Layout standard</span>
          </button>
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className={`px-3 py-1.5 rounded text-sm ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-[#787774] hover:bg-[rgba(55,53,47,0.08)]'}`}
          >
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
}
